import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from './BackButton.js';
import './index.css';

const LINK_BASE = 'http://undergroundradio.us/musicv2/?token=';

function Juugs() {
    const navigate = useNavigate();
    const [email, setEmail] = useState(null);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [juugs, setJuugs] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());
    const [selected, setSelected] = useState(() => new Set());
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [copiedToken, setCopiedToken] = useState(null);

    // WHO'S LOGGED IN
    useEffect(() => {
      (async () => {
        try {
          const res = await fetch('/auth/me', { credentials: 'include' });
          if (!res.ok) {
            navigate('/login', { replace: true });
            return;
          }
          const data = await res.json();
          setEmail(data.email);
        } catch (err) {
          console.error(err);
          navigate('/login', { replace: true });
        } finally {
          setCheckingAuth(false);
        }
      })();
    }, [navigate]);

    // SHOW JUUGS
    useEffect(() => {
        if (!email) return;

        const fetchData = async () => {
            try {
                const res = await fetch('/juugs', { credentials: 'include' });

                if (!res.ok) {
                    console.log('JUUGS NOT SHOWIN');
                    return;
                }

                const data = await res.json();
                setJuugs(data.juug ?? []);
                setHasSearched(true);
                setSelected(new Set());
                setExpanded(new Set());
            } catch (err) {
                console.error(err);
            }
        };

        fetchData();
    }, [email]);

    async function logout() {
      try {
        await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
      } catch (err) {
        console.error(err);
      }
      navigate('/login', { replace: true });
    }

    function toggleExpanded(name) {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    }

    function toggleSelected(name) {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    }

    function toggleSelectAll() {
        setSelected(prev =>
            prev.size === juugs.length ? new Set() : new Set(juugs.map(j => j.name))
        );
    }

    async function copyLink(token) {
        try {
            await navigator.clipboard.writeText(`${LINK_BASE}${token}`);
            setCopiedToken(token);
            setTimeout(() => setCopiedToken(t => (t === token ? null : t)), 1500);
        } catch (err) {
            console.error(err);
            alert('Could not copy — copy the link manually instead.');
        }
    }

    // DELETE SELECTED JUUGS
    async function deleteSelected() {
        const names = Array.from(selected);
        try {
            const res = await fetch('/delete/bulk', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ names }),
            });

            if (!res.ok) {
                console.log('Did not delete');
                return;
            }

            setJuugs(prev => prev.filter(j => !selected.has(j.name)));
            setSelected(new Set());
            setConfirmingDelete(false);
        } catch (err) {
            console.error(err);
        }
    }

    const allSelected = juugs.length > 0 && selected.size === juugs.length;

    if (checkingAuth) return null;

    return (
        <main className="page">
          <div className="page-content juug-page-content">
            <BackButton fallback="/link" />
            <div className="card">
              <h1>Your JUUGS</h1>
              <p className='help-text'>Logged in as {email}</p>
              <button type='button' className='btn btn-secondary' onClick={logout}>Log out</button>
            </div>

            <section aria-label="Your JUUGS">
              {!hasSearched ? null : juugs.length === 0 ? (
                <p className="empty-state">No JUUGS found yet.</p>
              ) : (
                <>
                  <label className="select-all-row">
                    <input
                      type="checkbox"
                      className="juug-checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                    <span>{allSelected ? 'Deselect all' : `Select all (${juugs.length})`}</span>
                  </label>

                  <ul className="list">
                    {juugs.map((item) => {
                      const tokens = Array.isArray(item.token) ? item.token : (item.token ? [item.token] : []);
                      const songs = Array.isArray(item.song) ? item.song : [item.song];
                      const isExpanded = expanded.has(item.name);
                      const isSelected = selected.has(item.name);

                      return (
                        <li
                          key={item.name}
                          className={`list-item juug-item${isSelected ? ' juug-item-selected' : ''}`}
                        >
                          <div className="juug-header">
                            <input
                              type="checkbox"
                              className="juug-checkbox"
                              aria-label={`Select ${item.name}`}
                              checked={isSelected}
                              onChange={() => toggleSelected(item.name)}
                            />
                            <button
                              type="button"
                              className="juug-summary"
                              onClick={() => toggleExpanded(item.name)}
                              aria-expanded={isExpanded}
                            >
                              <span className="juug-name">{item.name}</span>
                              <span className="juug-meta">
                                <span className="badge">{songs.length} song{songs.length === 1 ? '' : 's'}</span>
                                <span className="badge">{tokens.length} link{tokens.length === 1 ? '' : 's'}</span>
                              </span>
                            </button>
                            <span className={`chevron${isExpanded ? ' chevron-open' : ''}`} aria-hidden="true">
                              &rsaquo;
                            </span>
                          </div>

                          {isExpanded && (
                            <div className="juug-details">
                              <p className="juug-songs"><strong>Songs:</strong> {songs.join(', ')}</p>
                              {tokens.length === 0 ? (
                                <p className="help-text">No links yet.</p>
                              ) : (
                                <ul className="juug-links">
                                  {tokens.map((token) => (
                                    <li key={token} className="link-row">
                                      <a
                                        className="link-url"
                                        href={`${LINK_BASE}${token}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {LINK_BASE}{token}
                                      </a>
                                      <button
                                        type="button"
                                        className="btn btn-secondary btn-copy"
                                        onClick={() => copyLink(token)}
                                      >
                                        {copiedToken === token ? 'Copied' : 'Copy'}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </section>
          </div>

          {selected.size > 0 && (
            <div className="bulk-bar" role="region" aria-label="Bulk actions">
              {confirmingDelete ? (
                <div className="bulk-bar-inner">
                  <p role="alert" className="bulk-bar-text">
                    Delete {selected.size} JUUG{selected.size === 1 ? "" : "S"}? This can't be undone.
                  </p>
                  <div className="btn-row">
                    <button type="button" className="btn btn-danger" onClick={deleteSelected}>
                      Yes, delete
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setConfirmingDelete(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bulk-bar-inner">
                  <span className="bulk-bar-text">{selected.size} selected</span>
                  <div className="btn-row">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setSelected(new Set())}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => setConfirmingDelete(true)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
    );
}

export default Juugs;
